import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider, connect } from 'react-redux';
import { store } from './redux/store';
import { actionCreators } from './redux/actions';
import INotesAppProps from './interfaces/notesAppProps';
import INotesAppState from './interfaces/notesAppState';
import TabName from './interfaces/tabName';
import { NoteService } from '../../services/noteService';
import AllNotesList from './allNotesList';
import ByCategoryList from './byCategoryList';
import EditList from './editList';
import TabHeader from './tabHeader';
import EditModal from './editModal';
import ExportModal from './exportModal';
import CreateModal from './createModal';
import ToastBar from './shared-components/toastbar';
import * as PrintContent from '../../modules/print/printContent';
import './css/notes.scss';
import { render } from '../../common/reactUtils';
import { noteTextToHtml } from '../../modules/helpers/noteTextFormatter';
import { NOTE_COLOR_NAMES } from '../../common/consts';

interface ILocalState {
    selectedColors: string[];
}

class NotesApp extends React.Component<INotesAppProps, ILocalState> {
    noteService: NoteService;
    constructor(props: INotesAppProps) {
        super(props);
        this.state = { selectedColors: [] };
        this.noteService = new NoteService();
        this.loadData = this.loadData.bind(this);
        this.removeRootNode = this.removeRootNode.bind(this);
        this.renderTab = this.renderTab.bind(this);
    }

    /**
     * DO NOT REMOVE. This code seems weird but is intentional. Explanation:
     * `removeRootNode()` replaces the mounting node with all of it's children. 
     * The reasoning behind this is that we do not want to change the CSS rules, R
     * which are clean BEM, just to accomodate for eventual root containers.
     * 
     * If you remove this, the CSS will break, which jQuery have a hard dependency on, 
     * and the sidebar won't display the list of bookmarks.
     * */
    removeRootNode() {
        const node: any = document.getElementById('js-react-notes-app');
        if (node) {
            node.replaceWith(...node.childNodes);
        }
    }

    loadData() {
        this.props.toggleAllNotesIsLoading();
        this.noteService.getNotes((data) => {
            if (data && data.length > 0) {
                this.props.setAllNotes(data);
            }
            this.removeRootNode();
            this.props.toggleAllNotesIsLoading();
            this.noteService.getNoteCategories((data) => {
                this.props.setAllCategories(data);
            }, (err) => {
                console.log('could not get categories', err)
            })
        }, (error) => {
            this.removeRootNode();
            this.props.toggleAllNotesIsLoading();
        });
    }

    componentDidMount(): void {
        this.loadData();
    }

    componentDidCatch(error, info): void {
        console.error({ error, info });
    }

    renderTab(name) {
        
        switch (name) {
            default:
                return <AllNotesList />;
        }
    }

    private handleSelectColor = (color: string) => {
        let newColors: string[];
        if (this.state.selectedColors.includes(color)) {
            newColors = this.state.selectedColors.filter(c => c !== color);
        } else {
            newColors = [...this.state.selectedColors, color];
        }
        this.setState({ selectedColors: newColors });

        const searchInput = document.querySelector<HTMLInputElement>('#notes-search');
        const searchText = searchInput ? searchInput.value : '';
        this.handleSearch(searchText, newColors);
    }

    // getNotes/getNoteCategories delar samma bakomliggande DbContext (NoteService är
    // registrerad som singleton), så anropen måste köras i sekvens - annars kastar EF
    // Core "A second operation was started on this context instance..." vid parallella anrop.
    private handleSearch = (text: string, colors: string[] = this.state.selectedColors) => {
        const selectedColors = colors.length > 0 ? colors : undefined;
        if (text) {
            this.noteService.getFilteredNotes(text, (data) => {
                if (data) {
                    this.props.setAllNotes(data);
                }
                this.noteService.getFilteredNotesByCategory(text, (catData) => {
                    if (catData) {
                        this.props.setAllCategories?.(catData);
                    }
                }, (err) => {
                    console.log('could not get filtered categories', err);
                }, selectedColors);
            }, (err) => {
                console.log('could not get filtered notes', err);
            }, selectedColors);
        } else {
            this.noteService.getNotes((data) => {
                if (data) {
                    this.props.setAllNotes(data);
                }
                this.noteService.getNoteCategories((catData) => {
                    if (catData) {
                        this.props.setAllCategories?.(catData);
                    }
                }, (err) => {
                    console.log('could not get categories', err);
                }, selectedColors);
            }, (err) => {
                console.log('could not get notes', err);
            }, selectedColors);
        }
    }

    render() {
        const examModeContainer = document.getElementById("exam-mode-container");
        let examModeContent: string | null = null;

        if (examModeContainer && !examModeContainer.classList.contains("exam-mode-container_consultant")) {
            examModeContent = examModeContainer.innerHTML;
        }

        const colors = ['color1', 'color2', 'color3', 'color4', 'color5'];

        return (
            <React.Fragment>
                <EditModal />
                <CreateModal />
                <ExportModal />
                <ToastBar />
                <div className="personal__subnavigation  personal__subnavigation--expanded notesSubmenu" id="js-react-notes-expanded">
                    <div className="notifications  notifications--notes">
                        <div id="sidebar-notes" className="notifications__list-wrapper  notifications__list-wrapper--flex">
                            <TabHeader visibleTabName={this.props.visibleTabName}
                                onClickAll={(event) => {
                                    event.preventDefault();
                                    this.props.setVisibleTabName(TabName.ALL);
                                }}
                                onClickEdit={(event) => {
                                    event.preventDefault();
                                    this.props.setVisibleTabName(TabName.EDIT);
                                }}
                                onClickByCategory={(event) => {
                                    event.preventDefault();
                                    this.props.setVisibleTabName(TabName.BY_CATEGORY);
                                }}
                            />
                            {examModeContent && (
                                <div
                                    dangerouslySetInnerHTML={{ __html: examModeContent }}
                                />
                            )}
                            {!examModeContent && (
                                <>
                                    <div>
                                        <div className="notifications__functions">
                                            <form className="notifications__search-form" onSubmit={(event) => {
                                                event.preventDefault();
                                                const inputText = event.target[0].value;
                                                this.handleSearch(inputText);
                                            }}>
                                                <label htmlFor="notes-search">Sök bland anteckningar</label>
                                                <div className="notifications__search-field">
                                                    <input
                                                        id="notes-search"
                                                        type="search"
                                                        className="notifications__search-text"
                                                        aria-label="sök bland anteckningar"
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Enter') {
                                                                event.preventDefault();
                                                                const button = document.querySelector<HTMLButtonElement>('.notifications__search-btn');

                                                                if (button) {
                                                                    button.click();
                                                                }
                                                            }
                                                        }}
                                                    />
                                                    <button type="submit" className="notifications__search-btn">Sök</button>
                                                </div>
                                            </form>
                                            <div className="notifications__color-filter">
                                                <div id="color-filter-label">Filtrera på markeringsfärg</div>
                                                <div className="color-selector">
                                                    <div className="color-selector__colors" role="group" aria-labelledby="color-filter-label">
                                                        {colors.map((color, index) => {
                                                            const isSelected = this.state.selectedColors.includes(color);
                                                            return (
                                                                <button
                                                                    type="button"
                                                                    aria-label={NOTE_COLOR_NAMES[color] || color}
                                                                    aria-pressed={isSelected}
                                                                    aria-setsize={colors.length}
                                                                    aria-posinset={index + 1}
                                                                    onClick={() => this.handleSelectColor(color)}
                                                                    className={`color-selector__color  color-selector__color--${color}${isSelected ? '  color-selector__color--selected' : ''}`}
                                                                    key={color}
                                                                ></button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                             </div>
                                        </div>
                                        {<div className={`notifications__functions-wrapper ${this.props.selectedNotes.length > 0 ? 'notifications__functions-wrapper--visible' : ''}`}>
                                            <button type="button" className="notifications__function  notifications__function--close" onClick={(event) => {event.preventDefault();this.props.clearSelectedNotes();}} aria-label="Stäng Anteckningar" title="Stäng"></button>
                                            <div className="notifications__function--text" aria-hidden="true">Radera {this.props.selectedNotes.length} anteckningar</div>
                                            <button type="button" className="notifications__function  notifications__function--delete" onClick={(event) => this.props.deleteNotes(event, this.props.selectedNotes)} aria-label={`Radera ${this.props.selectedNotes.length} anteckningar`} title="Ta bort">Radera</button>
                                        </div>}
                                    </div>

                                    {
                                        this.renderTab(this.props.visibleTabName)
                                    }
                                </>
                            )}

                        </div>
                    </div>
                </div>
            </React.Fragment>
        )
    }
}

const mapDispatchToProps = (dispatch, ownProps): INotesAppProps => {
    const noteService = new NoteService();
    return {
        ...ownProps,
        deleteNotes: (event, selectedNotes) => {
            event.preventDefault()
            if (selectedNotes && selectedNotes.length > 0) {
                // Anropen körs i sekvens (nästa startar först när föregående är klart)
                // istället för parallellt, eftersom parallella DELETE-anrop kunde krocka
                // mot samma DbContext-instans på servern och ge InvalidOperationException.
                const idsToDelete = [...selectedNotes];

                const deleteNext = () => {
                    if (idsToDelete.length === 0) {
                        // getNoteCategories() avfyras först efter att getNotes() svarat
                        // (istället för samtidigt), eftersom parallella GET-anrop kunde
                        // krocka mot samma DbContext-instans på servern.
                        noteService.getNotes((data) => {
                            if (data) {
                                dispatch(actionCreators.setAllNotes(data));
                            }
                            noteService.getNoteCategories((catData) => {
                                dispatch(actionCreators.setAllCategories(catData));
                            }, (err) => {
                                console.log('could not get categories', err)
                            })
                        }, (error) => {
                            console.error({ error });
                        });
                        return;
                    }

                    const id = idsToDelete.shift();
                    noteService.deleteNoteById(id, () => {
                        dispatch(actionCreators.setSelectedNotes(id))
                        deleteNext();
                    }, () => {
                        alert('Tillfälligt fel: Kan inte ta bort anteckning');
                        deleteNext();
                    });
                };

                deleteNext();
            }
        },
        toggleExportModal: (event, selectedNotes) => {
            if (event) {
                event.preventDefault()
            }

            if (!selectedNotes || selectedNotes.length === 0) {
                dispatch(actionCreators.setToastBarType('warn'));
                dispatch(actionCreators.setToastBarMessage('Inga anteckningar valda för export'));
                dispatch(actionCreators.toggleToastBar());
            }
            else {
                dispatch(actionCreators.toggleExportModal())
            }
        },
        printNotes: (event, selectedNotes, notes) => {
            event.preventDefault()
            if (selectedNotes && selectedNotes.length > 0) {
                const sum = []
                notes.forEach(item => {
                    item.notes.forEach(note => {
                        note.pageHeading = item.pageHeading
                        sum.push(note)
                    })
                })
                const filtered = [...sum].filter(note => selectedNotes.indexOf(note.id) > -1)
                const print = filtered.map((note) => {
                    let title: string = '';
                    let text: string = '';
                    if (note.title) {
                        title = note.title;
                    }

                    if (note.text) {
                        text = noteTextToHtml(note.text);
                    }
                    else if (note.selection) {
                        try {
                            text = `<i>Markering:</i> ${JSON.parse(note.selection).text}`;
                        }
                        finally { }
                    }
                    return `<div class="print-note"><div class="print-note__document">${note.pageHeading}</div>${title !== '' ? `<div class="print-note__title">${title}</div>` : ''}<div class="print-note__text">${text}</div></div>`;
                }).join('')
                PrintContent.print(print);
            } else {
                alert('Markera vilka anteckningar du vill skriva ut')
            }
        },
        toggleFunctionsToolbar: (event) => {
            event.preventDefault();
            dispatch(actionCreators.toggleFunctionsToolbar())
        },
        setAllNotes: (notes) => dispatch(actionCreators.setAllNotes(notes)),
        setAllCategories: (categories) => dispatch(actionCreators.setAllCategories(categories)),
        toggleAllNotesIsLoading: () => dispatch(actionCreators.toggleAllNotesListIsLoading()),
        setVisibleTabName: (name: TabName) => dispatch(actionCreators.setVisibleTabName(name)),
        searchForNotes: (text: string) => {
            if (text) {
                noteService.getFilteredNotes(text, (data) => {
                    if (data) {
                        dispatch(actionCreators.setAllNotes(data));
                        dispatch(actionCreators.setNoSearchHits(data.length === 0));
                    }
                }, (err) => {
                    console.log({ filteredNotesError: err })
                });
                noteService.getFilteredNotesByCategory(text, (data) => {
                    if (data) {
                        dispatch(actionCreators.setAllCategories(data));
                    }
                }, (err) => {
                    console.log({ filteredNotesError: err })
                });
            } else {
                noteService.getNotes((data) => {
                    if (data) {
                        dispatch(actionCreators.setAllNotes(data));
                        dispatch(actionCreators.setNoSearchHits(false));
                    }
                }, (error) => {
                    console.error({ error });
                });
                noteService.getNoteCategories((data) => {
                    if (data) {
                        dispatch(actionCreators.setAllCategories(data));
                    }
                }, (error) => {
                    console.error({ error });
                });
            }
        },
        clearSelectedNotes: () => {
            dispatch(actionCreators.clearSelectedNotes());
        },
    };
}


const mapStateToProps = (state: INotesAppState): INotesAppProps => {
    return { visibleTabName: state.visibleTabName, displayFunctionsToolbar: state.displayFunctionsToolbar, selectedNotes: state.selectedNotes, notes: state.notes };
}

const ConnectedApp = connect(mapStateToProps, mapDispatchToProps)(NotesApp);

export const initialize = () => {
    
        render(
            <Provider store={store}>
                <ConnectedApp />
            </Provider>,
            '#js-react-notes-app'
        );
    
}